import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFormContext } from "react-hook-form";
import { CircularProgress } from "@mui/material";

import AmountField from "../../Components/Form Fields/amount-field";
import PrimaryButton from "../../Components/Buttons/primary-button";
import FormSwitch from "../../Components/Form Fields/switch";

import renderAuthorizeTransfer from "../../Organisms/Modals/Transfer/authorize-transfer";
import renderStatusModal from "../../Organisms/Modals/status-modal";
import renderAstraSignUp from "../../Organisms/Modals/Cards/astra-sign-up";
import renderMandateBankAccountAddition from "../../Organisms/Modals/Bank Account/mandate-bank-account-addition";
import renderGetRecurringDepositPreferences from "../../Organisms/Modals/Recurring Deposits/get-recurring-deposit-preferences";
import renderInstantTransferIneligible from "../../Organisms/Modals/Cards/instant-transfer-ineligible";
import renderInstantTransferLimitReached from "../../Organisms/Modals/Cards/instant-transfer-limit-reached";
import renderSelectConnectedAccounts from "../../Organisms/Modals/Bank Account/select-connected-accounts";
import renderSelectDebitCards from "../../Organisms/Modals/Cards/select-debit-cards";

import useAstra from "../../CustomHooks/useAstra";

import withFormProvider from "../../HOC/withFormProvider";

import {
  useConnectedAccounts,
  useConnectedAccountSelection,
  useStakeDebit,
  useDeals,
  useGetRecurringDeposit,
  useBankingLimits,
} from "../../CustomHooks/Queries";
import {
  useInitiateACHDebit,
  useCreateTransferRoutine,
  useActivateRecurringDeposit,
} from "../../CustomHooks/Mutations";

import { useModalContext } from "../../Contexts/ModalContext";
import { useAppUtilityContext } from "../../Contexts/AppUtilityContext";
import { useAdminStateSafe } from "../../Contexts/AdminStateContext";

import { getTransferRoutinePayload } from "../../Utils/api-payload-generators";
import {
  getSelectedAstraCard,
  getActiveSVDAstraDebitCard,
  dispatchErrorToast,
  formatCurrency,
  getTransferTypes,
  isSVDApproved,
  isSelectedTransferMethodInstant,
  isSelectedTransferMethodBank,
  getAstraCardsWithSystemCardFiltered,
  formatDate,
  appendISOTime,
} from "../../Utils/helpers";
import { getLastFour } from "../../Utils/pii";
import {
  FailedRecurringDepositStatusModalProps,
  FailedTransferStatusModalProps,
  nilYearLongMonthDateFormat,
  SuccessfulRecurringDepositStatusModalProps,
  SuccessfulTransferStatusModalProps,
} from "../../Utils/constants";

import { ChevronLeft, RecurringSchedule } from "../../Assets/Icons";

function AddCash() {
  const recurringDepositAttributesRef = useRef(null);

  const modalContext = useModalContext();

  const { updateModalOnCloseCallBack, onModalClose, popModalStack } = modalContext;

  const navigate = useNavigate();

  const { appUtilityProps, setAppUtilityProps } = useAppUtilityContext();

  const [selectedTransferType, setSelectedTransferType] = useState("ach");

  const { watch, handleSubmit } = useFormContext();

  const [isRecurringTransaction, setIsRecurringTransaction] = useState(false);
  const [recurringSchedule, setRecurringSchedule] = useState(null);

  const {
    isCreatingUserIntent: isAstraCreatingUserIntent,
    isLoadingRoutesCapabilities,
    isCreatingTrustedAuthenticationToken,
    isLoadingAstraCards,
    isInstantTransferIneligible,
    openAstraPortal,
    astraDebitCards,
  } = useAstra();

  const { isLoading: isLoadingDeals, data: deals } = useDeals();

  const { isFetching: isFetchingStakeDebit, data: stakeDebit } = useStakeDebit();
  
  const { isLoading: isLoadingBankingLimits, data: bankingLimits } = useBankingLimits();

  const { isLoading: isLoadingConnectedAccounts, data: connectedAccounts } = useConnectedAccounts();

  const { isLoading: isLoadingConnectedAccountSelection, data: connectedAccountSelection } =
    useConnectedAccountSelection();

  const {
    isLoading: isLoadingRecurringDeposit,
    data: recurringDeposit,
    refetch: refetchRecurringDeposit,
  } = useGetRecurringDeposit();

  const isAstraAccountConnected = useMemo(
    () => getAstraCardsWithSystemCardFiltered(astraDebitCards, stakeDebit).length > 0,
    [astraDebitCards, stakeDebit],
  );

  const isBankAccountConnected = useMemo(() => {
    return connectedAccounts?.data?.data?.accounts?.length > 0;
  }, [connectedAccounts?.data?.data?.accounts]);

  const selectedAccount = useMemo(
    () =>
      connectedAccounts?.data?.data?.accounts?.find(
        (account) =>
          account.account_id === appUtilityProps?.selectedAccountId ?? connectedAccountSelection?.data?.data?.selected,
      ) ??
      connectedAccounts?.data?.data?.accounts?.[0] ??
      null,
    [
      appUtilityProps?.selectedAccountId,
      connectedAccounts?.data?.data?.accounts,
      connectedAccountSelection?.data?.data?.selected,
    ],
  );

  const SVDApproved = useMemo(() => {
    return isSVDApproved(stakeDebit?.data);
  }, [stakeDebit?.data]);

  const dailyAddFundsLimit = useMemo(() => {
    const dailyLimit = (bankingLimits?.daily_add_cash_limit ?? 0) / 100;
    return dailyLimit;
  }, [bankingLimits]);

  const selectedAstraCard = useMemo(
    () => getSelectedAstraCard(astraDebitCards, stakeDebit, appUtilityProps?.selectedCardId),
    [astraDebitCards, stakeDebit, appUtilityProps?.selectedCardId],
  );

  const accountDescription = useMemo(() => {
    return !!selectedAccount ? `${selectedAccount?.name} (...${selectedAccount?.mask})` : "";
  }, [selectedAccount]);

  const instantAccountDescription = useMemo(() => {
    return !!selectedAstraCard ? `${selectedAstraCard?.card_company} (...${selectedAstraCard?.last_four_digits})` : "";
  }, [selectedAstraCard]);

  const adminState = useAdminStateSafe();
  const isMockInstantOff = adminState?.isMockMode && adminState?.selInstantCashOut === "ico-off";

  const transferTypes = useMemo(() => {
    const types = getTransferTypes(deals?.data);
    if (isMockInstantOff) return types.filter((t) => t.type !== "instant");
    return types;
  }, [deals, isMockInstantOff]);

  useEffect(() => {
    if (!isFetchingStakeDebit && !SVDApproved) {
      navigate("/home");
    }
  }, [isFetchingStakeDebit, SVDApproved, navigate]);

  const GoToBanking = () => navigate("/home");

  const onAddCashSuccess = (description) => {
    updateModalOnCloseCallBack(GoToBanking);
    renderStatusModal(modalContext, {
      ...SuccessfulTransferStatusModalProps,
      description,
      onCTAButtonClick: () => onModalClose({ callOnClose: GoToBanking }),
    });
  };

  const onAddCashError = (errorProps = {}) =>
    renderStatusModal(modalContext, {
      ...FailedTransferStatusModalProps,
      ...errorProps,
      onCTAButtonClick: () => onModalClose(),
    });

  const { mutate: InitiateACHDebit } = useInitiateACHDebit({
    onSuccess: () =>
      onAddCashSuccess(`Your transfer is expected to arrive on ${deals?.data?.content?.transaction_clearing_date}.`),
    onError: (err) => onAddCashError(err?.response?.status === 422 ? { description: err?.response?.data?.error } : {}),
  });

  const { mutate: CreateTransferRoutine } = useCreateTransferRoutine({
    onSuccess: () => onAddCashSuccess("Your transfer is expected to arrive in a few minutes."),
    onError: (err) => {
      if (err?.response?.status === 429) renderInstantTransferLimitReached(modalContext);
      else onAddCashError();
    },
  });

  const { mutate: ActivateRecurringDeposit } = useActivateRecurringDeposit({
    onSuccess: (_, v) => {
      updateModalOnCloseCallBack(GoToBanking);
      renderStatusModal(modalContext, {
        ...SuccessfulRecurringDepositStatusModalProps,
        description: `Your first deposit is scheduled for ${formatDate(
          appendISOTime(v.start_date),
          nilYearLongMonthDateFormat,
        )}.`,
        onCTAButtonClick: () => onModalClose({ callOnClose: () => navigate("/recurring-deposits") }),
        onSecondaryCTAButtonClick: () => onModalClose({ callOnClose: GoToBanking }),
      });
      refetchRecurringDeposit();
    },
    onError: () =>
      renderStatusModal(modalContext, {
        ...FailedRecurringDepositStatusModalProps,
        onCTAButtonClick: () => popModalStack(0),
        onSecondaryCTAButtonClick: () => onModalClose(),
      }),
  });

  const cashAmount = parseFloat(watch("cash-amount")?.unmasked ?? "");

  const disableSubmitButton = isNaN(cashAmount) || cashAmount === 0;

  useEffect(() => {
    if (isSelectedTransferMethodInstant(selectedTransferType) && isInstantTransferIneligible) {
      setSelectedTransferType("ach");
    }
  }, [selectedTransferType, isInstantTransferIneligible]);

  const openAuthorizeTransferModal = () => {
    renderAuthorizeTransfer(modalContext, {
      stake_account_number_last4: getLastFour(stakeDebit?.data?.data?.account_number),
      stake_routing_number: stakeDebit?.data?.data?.routing_number,
      ...(isSelectedTransferMethodInstant(selectedTransferType) ? { selectedAstraCard } : { selectedAccount }),
      clearingTransactionDate: deals?.data?.content?.transaction_clearing_date,
      amount: cashAmount,
      isRecurringDeposit: isRecurringTransaction,
      recurringDepositPreferences: recurringDepositAttributesRef.current?.preferences,
      AuthorizeCallback: () => {
        if (isSelectedTransferMethodInstant(selectedTransferType)) {
          const svdAstraCard = getActiveSVDAstraDebitCard(astraDebitCards, stakeDebit);
          CreateTransferRoutine(
            getTransferRoutinePayload({
              sourceCard: svdAstraCard,
              destinationCard: selectedAstraCard,
              amount: cashAmount,
            }),
          );
        } else {
          if (isRecurringTransaction) {
            ActivateRecurringDeposit(recurringDepositAttributesRef.current?.preferences);
          } else {
            InitiateACHDebit({
              accountId: selectedAccount.account_id,
              amount: cashAmount,
            });
          }
        }
      },
    });
  };

  const onSubmit = () => {
    if (isRecurringTransaction) {
      recurringDepositAttributesRef.current = {
        preferences: {
          ...(recurringDepositAttributesRef.current.preferences ?? {}),
          amount: cashAmount,
          originating_account_id: selectedAccount.account_id,
        },
      };
    }

    if (
      (isSelectedTransferMethodBank(selectedTransferType) && !selectedAccount) ||
      (isSelectedTransferMethodInstant(selectedTransferType) && !selectedAstraCard)
    ) {
      dispatchErrorToast(
        isSelectedTransferMethodInstant(selectedTransferType)
          ? "Please add a debit card to proceed"
          : "Please add a bank account to proceed",
        {
          alignToAppContent: true,
          key: isSelectedTransferMethodInstant(selectedTransferType) ? "noAstraDebitCard" : "noConnectedAccount",
        },
      );
    } else if (dailyAddFundsLimit > 0 && cashAmount > dailyAddFundsLimit) {
      dispatchErrorToast("Amount cannot exceed the daily limit.", {
        alignToAppContent: true,
        key: "amountExceedsThreshold",
      });
    } else {
      setTimeout(() => openAuthorizeTransferModal(), 750);
    }
  };

  const handleChangeTransferType = (selectedType) => {
    if (isSelectedTransferMethodInstant(selectedType) && isInstantTransferIneligible) {
      renderInstantTransferIneligible(modalContext);
      return;
    }

    if (isSelectedTransferMethodInstant(selectedType) && !isAstraAccountConnected) {
      setTimeout(() => renderAstraSignUp(modalContext, openAstraPortal), 750);
    }

    if (isSelectedTransferMethodInstant(selectedType) && isRecurringTransaction) {
      setIsRecurringTransaction(false);
    }

    setSelectedTransferType(selectedType);
    setAppUtilityProps((p) => ({
      ...p,
      preferredTransferType: selectedType,
    }));
  };

  const handleSelectPaymentEntity = () => {
    if (isSelectedTransferMethodInstant(selectedTransferType)) {
      if (isAstraAccountConnected) {
        renderSelectDebitCards(modalContext);
      } else {
        renderAstraSignUp(modalContext, openAstraPortal);
      }
    } else {
      renderSelectConnectedAccounts(modalContext);
    }
  };

  const openGetRecurringDepositPreferencesModal = (initialValues) =>
    renderGetRecurringDepositPreferences(modalContext, {
      ...(initialValues ? { initialValues } : {}),
      onSubmit: ({ startDate, frequency }) => {
        recurringDepositAttributesRef.current = {
          preferences: {
            frequency_type: frequency,
            start_date: startDate,
          },
        };
        setIsRecurringTransaction(true);
        setRecurringSchedule({ frequency, startDate });
        onModalClose();
      },
    });

  const handleToggleRecurring = () => {
    if (!isBankAccountConnected) {
      renderMandateBankAccountAddition(modalContext, {
        title: "Bank Account Required",
        description:
          "To set up a recurring deposit, you must first connect a bank account. Please add your bank account to continue.",
        onSuccess: openGetRecurringDepositPreferencesModal,
      });
    } else if (!isRecurringTransaction) {
      openGetRecurringDepositPreferencesModal();
    } else {
      setIsRecurringTransaction((p) => !p);
    }
  };

  const hasFreeInstantUse = !(deals?.data?.data?.currentDeal?.has_used_1_free_instant_transfer__c ?? true);

  const feeLabel = useMemo(() => {
    const feePercentage = deals?.data?.data?.currentDeal?.INSTANT_TRANSFER_OUT_OF_STAKE_FEE_RATE ?? 0.0175;

    if (isSelectedTransferMethodInstant(selectedTransferType) && hasFreeInstantUse) {
      return "Free";
    } else if (isSelectedTransferMethodInstant(selectedTransferType) && isNaN(cashAmount)) {
      return `${(feePercentage * 100).toFixed(2)}%`;
    } else if (isSelectedTransferMethodInstant(selectedTransferType)) {
      return formatCurrency(cashAmount * feePercentage);
    } else {
      return "Free";
    }
  }, [deals, cashAmount, selectedTransferType, hasFreeInstantUse]);

  const cashAmountIncludingFees = useMemo(() => {
    const feePercentage = deals?.data?.data?.currentDeal?.INSTANT_TRANSFER_INTO_STAKE_FEE_RATE ?? 0;

    if (isSelectedTransferMethodInstant(selectedTransferType)) {
      if (isNaN(cashAmount)) {
        return 0;
      }

      return cashAmount + cashAmount * feePercentage;
    } else {
      return cashAmount;
    }
  }, [cashAmount, selectedTransferType, deals]);

  const transferAmountLabel = useMemo(() => {
    return isRecurringTransaction ? "Schedule" : "Add Cash";
  }, [isRecurringTransaction]);

  const isLoading =
    isLoadingDeals ||
    isFetchingStakeDebit ||
    isLoadingConnectedAccounts ||
    isLoadingConnectedAccountSelection ||
    isLoadingAstraCards ||
    isLoadingRecurringDeposit ||
    isLoadingBankingLimits;

  const [animReady, setAnimReady] = useState(false);
  const hasTriggeredAnim = useRef(false);

  useEffect(() => {
    if (!isLoading && !hasTriggeredAnim.current) {
      hasTriggeredAnim.current = true;
      requestAnimationFrame(() => setAnimReady(true));
    }
  }, [isLoading]);

  const isSettingUpAstra =
    isAstraCreatingUserIntent || isLoadingRoutesCapabilities || isCreatingTrustedAuthenticationToken;

  return (
    <div className={`add-cash${animReady ? " anim-ready" : ""}`}>
      <div className="ac-header">
        <button className="ac-back-button" type="button" onClick={() => navigate(-1)} aria-label="Go back">
          <ChevronLeft />
        </button>
        <span className="ac-title">Add Cash</span>
      </div>
      {isLoading ? (
        <div className="ac-loading">
          <CircularProgress />
        </div>
      ) : (
        <form className="ac-body" onSubmit={handleSubmit(onSubmit)} autoComplete="off">
          <div className="ac-amount-section">
            <AmountField />
            <span className={`ac-daily-limit${!!dailyAddFundsLimit && !isNaN(cashAmount) && cashAmount > dailyAddFundsLimit ? " visible" : ""}`}>
              {formatCurrency(dailyAddFundsLimit || 0, 0)} daily limit
            </span>
            {isSelectedTransferMethodInstant(selectedTransferType) && (
              <span className="ac-max-transfers-badge">Max Transfer Limit: 2 per day</span>
            )}
          </div>
          {transferTypes.length > 1 && (
            <div className="ac-speed-selection">
              {transferTypes.map((transferType, idx) => (
                <button
                  key={idx}
                  className={`ac-speed-card ${transferType.type}${
                    transferType.type === selectedTransferType ? " selected" : ""
                  }`}
                  type="button"
                  onClick={handleChangeTransferType.bind(this, transferType.type)}
                  disabled={isSelectedTransferMethodInstant(transferType.type) && isSettingUpAstra}
                >
                  <span className="ac-speed-icon">
                    <transferType.Icon className="icon" />
                  </span>
                  <span className="ac-speed-title">{transferType.title}</span>
                  {isSelectedTransferMethodInstant(transferType.type) && isSettingUpAstra ? (
                    <CircularProgress />
                  ) : (
                    <span className="ac-speed-desc">{transferType.description}</span>
                  )}
                  {isSelectedTransferMethodInstant(transferType.type) && isInstantTransferIneligible && (
                    <span className="ac-restricted-badge">Restricted</span>
                  )}
                </button>
              ))}
            </div>
          )}
          <div className="ac-details-card">
            {(() => {
              const hasAccount =
                (isSelectedTransferMethodInstant(selectedTransferType) && !!selectedAstraCard) ||
                (isSelectedTransferMethodBank(selectedTransferType) && !!selectedAccount);
              return (
                <button className="ac-detail-row ac-account-row" type="button" onClick={handleSelectPaymentEntity}>
                  <span className="ac-detail-label">From</span>
                  <span className={`ac-detail-value${!hasAccount ? " ac-link-text" : ""}`}>
                    {hasAccount
                      ? isSelectedTransferMethodInstant(selectedTransferType)
                        ? instantAccountDescription
                        : accountDescription
                      : isSelectedTransferMethodInstant(selectedTransferType)
                        ? "Link a card"
                        : "Link a bank"}
                    <svg className="ac-row-chevron" width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M6 4l4 4-4 4" stroke="#8B8FA3" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                </button>
              );
            })()}
            <div className="ac-divider" />
            <div className="ac-detail-row">
              <span className="ac-detail-label">To</span>
              <span className="ac-detail-value">Stake</span>
            </div>
            <div className="ac-divider" />
            <div className="ac-detail-row">
              <span className="ac-detail-label">Fee</span>
              <span className="ac-detail-value ac-fee-value">
                {isSelectedTransferMethodInstant(selectedTransferType) ? feeLabel : "Free"}
              </span>
            </div>
            {!isSelectedTransferMethodInstant(selectedTransferType) &&
              !recurringDeposit?.data?.data?.active && (
                <>
                  <div className="ac-divider" />
                  <div className="ac-detail-row">
                    <span className="ac-detail-label">Make this recurring</span>
                    <FormSwitch checked={isRecurringTransaction} onClick={handleToggleRecurring} ariaLabel="Make this recurring" />
                  </div>
                  {isRecurringTransaction && recurringSchedule && (
                    <>
                      <div className="ac-divider" />
                      <button
                        type="button"
                        className="ac-detail-row ac-recurring-summary-row"
                        onClick={() => openGetRecurringDepositPreferencesModal({
                          startDate: recurringSchedule.startDate,
                          frequency: recurringSchedule.frequency,
                        })}
                      >
                        <span className="ac-recurring-summary">
                          <RecurringSchedule className="ac-recurring-icon" />
                          <span className="ac-recurring-text">
                            <span className="ac-recurring-freq">{recurringSchedule.frequency}</span>
                            <span className="ac-recurring-start">
                              Starting {formatDate(appendISOTime(recurringSchedule.startDate), nilYearLongMonthDateFormat)}
                            </span>
                          </span>
                        </span>
                        <span className="ac-recurring-edit">Edit</span>
                      </button>
                    </>
                  )}
                </>
              )}
            <div className="ac-divider" />
            <div className="ac-detail-row ac-disclaimer-row">
              <span className="ac-disclaimer">
                {isSelectedTransferMethodInstant(selectedTransferType)
                  ? "Transfer speed depends on your bank and could take up to 30 minutes."
                  : isRecurringTransaction
                    ? "Transfers take one business day to process.Transfers set up on a weekend, or holiday, will occur on the next business day."
                    : "Standard transfers take 1\u20133 business days. Requests after 3 PM ET or on weekends/holidays are processed the next business day."}
              </span>
            </div>
          </div>
          <PrimaryButton className="ac-submit-btn" type="submit" disabled={disableSubmitButton}>
            {transferAmountLabel}
          </PrimaryButton>
        </form>
      )}
    </div>
  );
}

export default withFormProvider(AddCash);
